import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { shareReplay } from 'rxjs';

// --- Interfaces ---
import type { ITokens, IUser } from '@interfaces/user';

export interface ILoginBody {
	email: IUser['email'];
	password: IUser['password'];
	rememberMe?: boolean;
}

export interface IRefreshTokenBody {
	accessToken: string;
	refreshToken: string;
}

export interface IGuestToken {
	token: string;
}

export interface IPublicApi {
	clientId: string;
	clientSecret: string;
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	constructor(private readonly _http: HttpClient) {}

	// --- POST endpoints ---
	login(body: ILoginBody, hostname: string) {
		return this._http
			.post<IUser>('auth/login', { ...body, hostname })
			.pipe(shareReplay());
	}

	guestToken(hostname: string) {
		const params = new HttpParams().set('hostname', hostname || '');
		return this._http
			.get<IGuestToken>('auth/guest-token', {
				params: params,
			})
			.pipe(shareReplay(1));
	}

	integrationTokens() {
		return this._http
			.get<IGuestToken>('auth/integration-tokens')
			.pipe(shareReplay(1));
	}

	publicApiCredentials() {
		return this._http
			.get<IPublicApi>('auth/public-api-credentials')
			.pipe(shareReplay(1));
	}

	retrievePassword(email: IUser['email'], hostname: string) {
		return this._http.post<{ message: string }>('auth/retrieve-password', {
			email,
			hostname,
		});
	}

	resetPassword(
		email: IUser['email'],
		code: string,
		newPassword: IUser['password'],
		hostname: string
	) {
		return this._http.post<{ message: string }>('auth/reset-password', {
			email,
			code,
			newPassword,
			hostname,
		});
	}

	refreshToken(body: IRefreshTokenBody) {
		return this._http.post<IUser>('auth/refresh', body);
	}

	revokeToken(id: IUser['id']) {
		return this._http.post(`auth/revoke/${id}`, null).pipe(shareReplay());
	}
}
